import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import Info from '@/assets/images/Info.svg';
import { Modal } from '@/components/ui/modal';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { GET_ALL_PROGRAM } from '@/Graphql/queries';
import { authState } from '@/vars/auth';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance, loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { CARD_SETUP_INTENT, PRODUCT_PAYMENT_INTENT } from '../GraphQL/mutation';
import { GET_ALL_PLANS, GET_MY_SAVED_CARD } from '../GraphQL/queries';
import CheckoutForm from './CheckoutForm';
interface BuySubsctriptionProps {
  open: boolean;
  defaultPlan?: string;
  selectedProgram?: string;
  handleClose: () => void;
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

const BuySubsctription: React.FC<BuySubsctriptionProps> = ({
  open,
  defaultPlan,
  selectedProgram: initialProgram,
  handleClose,
}) => {
  const [paymentStatus, setPaymentStatus] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan || '');
  const [selectedProgram, setSelectedProgram] = useState<string>(initialProgram || 'fitvarsity');
  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const { userDetails } = useReactiveVar(authState);

  const { data: programsData, loading: programsLoading } = useQuery(GET_ALL_PROGRAM, {
    variables: {
      limit: 10,
      offset: 0,
    },
  });

  const { data: plans, loading: planLoading } = useQuery(GET_ALL_PLANS, {
    variables: {
      sort: { sessionCount: 1 },
      filter: {
        ...(selectedProgramId && { programId: selectedProgramId }),
        status: true,
      },
    },
    skip: !selectedProgramId, // Skip query if no program is selected
    onCompleted: (res) => {
      if (selectedPlan) {
        res?.getAllPlan.find((item) => item?._id === selectedPlan)
          ? ''
          : setSelectedPlan(res?.getAllPlan[0]?._id || '');
      } else {
        setSelectedPlan(res?.getAllPlan[0]?._id || '');
      }
    },
  });
  useQuery(GET_MY_SAVED_CARD);
  const [fetchCardSetup] = useMutation(CARD_SETUP_INTENT);
  const [fetchPayemntIntent, { data: paymentIntent, loading: paymentIntentLoading }] =
    useMutation(PRODUCT_PAYMENT_INTENT);

  useEffect(() => {
    fetchCardSetup();
  }, []);

  useEffect(() => {
    if (initialProgram) {
      setSelectedProgram(initialProgram);
    }
  }, [initialProgram]);

  // Initialize program ID when programs data loads or when selectedProgram changes
  useEffect(() => {
    if (programsData?.getAllProgram && selectedProgram) {
      const program = programsData.getAllProgram.find(
        (p) => p?.programName?.toLowerCase() === selectedProgram.toLowerCase(),
      );
      if (program) {
        setSelectedProgramId(program._id);
      }
    }
  }, [programsData, selectedProgram]);

  useEffect(() => {
    if (selectedPlan && userDetails?._id) {
      fetchPayemntIntent({
        variables: {
          data: {
            planId: selectedPlan,
            userId: userDetails?._id,
          },
        },
      });
    }
  }, [selectedPlan, userDetails]);

  const appearance: Appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#0570de',
      colorBackground: '#151525',
      borderRadius: '12px',
    },
  };

  const loader = 'auto';

  // Generate programs from API data
  const programs =
    programsData?.getAllProgram?.map((program) => {
      const programName = program?.programName?.toLowerCase() || '';

      // Map program names to icons
      const getIcon = (name: string) => {
        switch (name) {
          case 'fitvarsity':
            return FitVarsityIcon;
          case 'fitcurve':
            return FitCurveIcon;
          case 'fitprime':
            return FitPrimeIcon;
          default:
            return FitVarsityIcon; // fallback
        }
      };

      return {
        id: programName,
        _id: program?._id || '',
        name: program?.programName || '',
        description: program?.description || '',
        icon: getIcon(programName),
      };
    }) || [];

  // Handler for program selection
  const handleProgramSelect = (program: any) => {
    setSelectedProgram(program.id);
    setSelectedProgramId(program._id);
  };

  return (
    <Modal title='Buy Subscriptions' open={open} onClose={handleClose} className='bg-[#151525]'>
      <div className='max-h-[calc(100vh-150px)] overflow-y-auto relative'>
        {paymentStatus === 'completed' ? (
          ''
        ) : (
          <div className='px-7'>
            {/* Select Program Section */}
            <div className='bg-[#00001C] flex flex-col gap-3 p-[18px] rounded-[20px] my-5'>
              <p className='text-white font-medium text-base'>Select Program</p>
              <div className='flex gap-2.5'>
                {programsLoading
                  ? Array(3)
                    .fill(0)
                    .map((_, index) => (
                      <Skeleton key={index} className='h-[120px] flex-1 bg-bgSecondary' />
                    ))
                  : programs.map((program) => (
                    <div
                      key={program.id}
                      className={`flex-1 flex flex-col gap-3 p-[18px_14px] rounded-xl cursor-pointer transition-all ${selectedProgram === program.id
                        ? 'bg-[#181A32] border-2 border-[#0A7AFF]'
                        : 'bg-gradient-to-br from-[#2F2F2F] to-[#2F2F2F]'
                        }`}
                      onClick={() => handleProgramSelect(program)}
                    >
                      <div className='flex items-center gap-3'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img src={program.icon} alt={program.name} className='w-8 h-8' />
                        </div>
                        <span className='text-white font-medium text-xl leading-[24px] tracking-[0.02em]'>
                          {program.name}
                        </span>
                      </div>
                      <p className='text-[#C5C8D0] text-xs leading-[17px]'>
                        {program.description}
                      </p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Select Package Section */}
            <div className='bg-[#00001C] flex flex-col gap-3 p-[18px] rounded-[20px] mb-5'>
              <div className='flex flex-col gap-2'>
                <p className='text-white font-medium text-base'>Select Package</p>
                <div className='flex gap-1.5'>
                  <div className='flex items-center gap-2.5 pt-[1px]'>
                    <img src={Info} alt='info' className='w-[18px] h-[18px]' />
                  </div>
                  <p className='text-[#E9CC7C] text-sm leading-[20px]'>
                    Sessions don&apos;t expire. Follow suggested pace to stay on track.
                  </p>
                </div>
              </div>
              <div className='flex gap-3'>
                {plans?.getAllPlan.map((plan) => {
                  const isSelected = selectedPlan === plan?._id;

                  return (
                    <div
                      key={plan?._id ?? ''}
                      className={`flex-1 flex justify-between items-center gap-3 p-[18px_14px] rounded-xl cursor-pointer transition-all ${isSelected
                        ? 'bg-[#181A32] border-2 border-[#0A7AFF]'
                        : 'bg-gradient-to-br from-[#2F2F2F] to-[#2F2F2F]'
                        }`}
                      onClick={() => setSelectedPlan(plan?._id || '')}
                    >
                      <div className='flex flex-col gap-1.5'>
                        <span className='text-white font-semibold text-base leading-[19px]'>
                          {plan?.sessionCount} Sessions
                        </span>
                        <span className='text-[#C5C8D0] font-medium text-xs leading-[15px]'>
                          Recommended: {plan?.recommendedNoOfDays} Days
                        </span>
                      </div>
                      <div className='flex flex-col items-end gap-0.5'>
                        {plan?.originalPrice !== 0 && (
                          <span className='text-[#E9CC7C] text-xs line-through'>
                            ${plan?.originalPrice}
                          </span>
                        )}
                        <span className='text-[#E9CC7C] font-bold text-base leading-[22px] tracking-[0.02em]'>
                          ${plan?.price}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <Separator className='bg-[#FFFFFF21] w-full' />
          </div>
        )}
        {planLoading || paymentIntentLoading ? (
          <PaymentLoading />
        ) : paymentIntent?.productPaymentIntent?.paymentIntentSecret ? (
          <Elements
            options={{
              clientSecret: paymentIntent?.productPaymentIntent?.paymentIntentSecret,
              appearance,
              loader,
              customerSessionClientSecret:
                paymentIntent?.productPaymentIntent?.customerSessionSecret,
            }}
            stripe={stripePromise}
          >
            {/* {paymentStatus === 'completed' ? (
              <CompletePage
                clientSecret={paymentIntent?.productPaymentIntent?.paymentIntentSecret}
                handleClose={handleClose}
              />
            ) : ( */}
            <CheckoutForm
              handleClose={handleClose}
              setPaymentStatus={setPaymentStatus}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              plans={plans}
              price={plans?.getAllPlan.find((item) => item?._id === selectedPlan)?.price || 0}
            />
            {/* )} */}
          </Elements>
        ) : (
          ''
        )}
      </div>
    </Modal>
  );
};

const PaymentLoading = () => (
  <div className='p-7'>
    <div className='w-full  bg-transparent shadow-lg rounded-lg p-6'>
      <Skeleton className='h-8 w-3/4 mb-4 bg-bgSecondary' />
      <Skeleton className='h-6 w-1/2 mb-4 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-12 w-full mb-4 bg-bgSecondary' />
      <Skeleton className='h-10 w-full bg-bgSecondary' />
    </div>
  </div>
);

export default BuySubsctription;
